import { useState, useEffect, useCallback, useMemo } from 'react';
import { useConfig, useAccount } from 'wagmi';
import { formatUnits, getAddress, type Hex } from 'viem';
import { useEnsureChain } from '../../../hooks/useEnsureChain';
import { useWriteDexIncreasePositionCollateral, dexAbi } from 'sc-interfaces';
import useAppStore, { useActiveExchangeAddress } from '../../../store/rootStore.ts';
import { useNotify } from '@/feature/notifications';
import { useTransactionState } from '../../../hooks/useTransactionMonitor';
import { useTokenFormatter } from '@/hooks/useTokenFormatter';
import { useAccountErrorHandler, accountReasonHandler } from '../../../hooks/useErrorHandler';
import { createRevertDecodingParams } from '../../../utils/decodeScError';
import { prepareContractWrite } from '../../../utils/contractWriteHelpers';
import { useWalletNonce } from '../../../hooks/useWalletNonce.ts';
import { telemetry } from '../../../utils/telemetry';

interface MarginData {
  marketId: number;
  amount: bigint;
  amountFormatted: string;
  /** Telemetry value from the raw units — `amountFormatted` is locale-grouped, which parseFloat truncates. */
  valueUsd?: number;
}

export function useAddMarginOnChain() {
  const config = useConfig();
  const notify = useNotify();
  const { address: accountAddress } = useAccount();
  const { getNonce } = useWalletNonce();
  const { ensureChain, configChainId } = useEnsureChain();

  const exchangeAddress = useActiveExchangeAddress();
  const exchangeToken = useAppStore((state) => state.exchange?.token);

  const formatToken = useTokenFormatter(exchangeToken);
  const displayOptions = useMemo(() => ({ minimumFractionDigits: 0 }), []);

  const { handleError: handleMarginError } = useAccountErrorHandler('margin addition');

  const [marginData, setMarginData] = useState<MarginData | null>(null);
  const [transactionData, setTransactionData] = useState<Hex | undefined>(undefined);

  const {
    writeContract: writeIncreaseMargin,
    isPending: isMarginPending,
    isError: isMarginError,
    isSuccess: isMarginSubmitted,
    data: txHash,
    reset: resetMargin,
  } = useWriteDexIncreasePositionCollateral({ config });

  const resetProcess = useCallback(() => {
    setCurrentStatus('idle');
    setMarginData(null);
    setTransactionData(undefined);
    resetMargin();
  }, [resetMargin]);

  const { currentStatus, setCurrentStatus, isPending, isSuccess, isError } = useTransactionState({
    isWritePending: isMarginPending,
    isWriteSuccess: isMarginSubmitted,
    isWriteError: isMarginError,
    txHash,
    processData: marginData,
    messages: {
      confirming: 'Adding margin to position...',
      confirmed: (data?: MarginData | null) => {
        if (data) {
          const market = useAppStore.getState().markets.get(data.marketId);
          telemetry.logMarginAddition({
            market: market?.symbol || 'Unknown',
            value_usd: data.valueUsd,
            symbol: exchangeToken?.symbol || 'Unknown',
          });
          return `Successfully added $${data.amountFormatted} margin to position.`;
        }
        return 'Margin added successfully.';
      },
      failed: 'Failed to add margin. Please try again.',
      rejected: 'Margin addition was rejected. Please try again.',
      error: 'Margin addition was cancelled.',
    },
    onSuccess: resetProcess,
    revertDecoding: createRevertDecodingParams(exchangeAddress, transactionData, accountAddress),
    reasonHandler: accountReasonHandler,
  });

  useEffect(() => {
    if (currentStatus === 'failed' || isError) {
      const timer = setTimeout(() => resetProcess(), 1000);
      return () => clearTimeout(timer);
    }
  }, [currentStatus, isError, resetProcess]);

  const addMargin = useCallback(
    async (amount: bigint, marketId: number) => {
      if (currentStatus !== 'idle') {
        notify('Another margin operation is already in progress', 'warning');
        return;
      }

      if (!marketId) {
        notify('No market selected', 'error');
        setCurrentStatus('failed');
        return;
      }

      const market = useAppStore.getState().markets.get(marketId);

      if (!market?.perpId) {
        notify('Market perpetual ID not available', 'error');
        setCurrentStatus('failed');
        return;
      }

      try {
        const marginOperationData: MarginData = {
          marketId,
          amount,
          amountFormatted: formatToken(amount, displayOptions),
          valueUsd: exchangeToken ? Number(formatUnits(amount, exchangeToken.decimals)) : undefined,
        };

        setMarginData(marginOperationData);
        setCurrentStatus('switching_chain');
        await ensureChain();
        setCurrentStatus('preparing');

        const prepared = await prepareContractWrite({
          config,
          address: exchangeAddress!,
          abi: dexAbi,
          functionName: 'increasePositionCollateral',
          args: [BigInt(market.perpId), amount] as const,
          account: accountAddress,
          chainId: configChainId,
        });

        setCurrentStatus('submitting');
        setTransactionData(prepared.transactionData);
        const nonce = await getNonce();
        writeIncreaseMargin({ ...(prepared.writeArgs as any), nonce } as any);
      } catch (error) {
        handleMarginError(error);
        setCurrentStatus('failed');
      }
    },
    [
      currentStatus,
      exchangeAddress,
      config,
      accountAddress,
      writeIncreaseMargin,
      setCurrentStatus,
      formatToken,
      exchangeToken,
      displayOptions,
      handleMarginError,
      getNonce,
      ensureChain,
      configChainId,
    ]
  );

  return {
    addMargin,
    resetProcess,
    currentStatus,
    marginData,
    isPending,
    isSuccess,
    isError,
  };
}
